import type { Row } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import { useAtomValue } from "jotai";
import { Fragment } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { cn } from "~/components/ui/cn";
import { useIsMobile } from "~/hooks/use-mobile";
import { brandState } from "~/stores/brand";

import type { OrderData } from "./order-table";

export const OrderTableRow: React.FC<{
  row: Row<OrderData>;
  columnPinning: string[];
}> = ({ row, columnPinning }) => {
  const isMobile = useIsMobile();
  const { search } = useLocation();

  const brand = useAtomValue(brandState);

  if (isMobile) {
    return (
      <NavLink
        to={`/${brand?.id ?? ""}/orders/${row.original.id}${search}`}
        className={cn(
          "cursor-pointer list-item space-y-3 rounded border border-gray-100 p-4",
          {
            "bg-white": row.original.requiredAction,
            "bg-[#FBFBFB]": !row.original.requiredAction,
          },
        )}
      >
        {/* Table row */}
        <div className="flex items-center gap-x-2">
          <div className="flex items-center whitespace-nowrap">
            {row.getVisibleCells().map((cell) =>
              cell.getContext().column.id === "createdAt" ? (
                <Fragment key={cell.id}>
                  {/* Table cell */}
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Fragment>
              ) : null,
            )}
          </div>
          <span className="text-xs text-[#9DA0A1]">•</span>
          <div className="truncate">
            {row.getVisibleCells().map((cell) =>
              cell.getContext().column.id === "id" ? (
                <Fragment key={cell.id}>
                  {/* Table cell */}
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Fragment>
              ) : null,
            )}
          </div>
        </div>
        {row.getVisibleCells().map((cell) =>
          cell.getContext().column.id === "recipientName" ? (
            <Fragment key={cell.id}>
              {/* Table cell */}
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </Fragment>
          ) : null,
        )}
        <div className="flex flex-wrap gap-2">
          {row.getVisibleCells().map((cell) =>
            cell.getContext().column.id === "fulfillment" ||
            cell.getContext().column.id === "delivery" ||
            cell.getContext().column.id === "claim" ? (
              <Fragment key={cell.id}>
                {/* Table cell */}
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Fragment>
            ) : null,
          )}
        </div>
        <div className="flex items-center justify-between gap-4">
          {row.getVisibleCells().map((cell) =>
            cell.getContext().column.id === "items" ? (
              <Fragment key={cell.id}>
                {/* Table cell */}
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Fragment>
            ) : null,
          )}
          {row.getVisibleCells().map((cell) =>
            cell.getContext().column.id === "total" ? (
              <Fragment key={cell.id}>
                {/* Table cell */}
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Fragment>
            ) : null,
          )}
        </div>
      </NavLink>
    );
  }

  return (
    <NavLink
      to={`/${brand?.id ?? ""}/orders/${row.original.id}${search}`}
      data-table-row
      data-state={row.getIsSelected() && "selected"}
      className={cn(
        "group table-row cursor-pointer border-b border-b-gray-100 last:border-none",
        {
          "bg-white": row.original.requiredAction,
          "bg-[#FBFBFB]": !row.original.requiredAction,
        },
      )}
    >
      {/* Table row */}
      {row.getVisibleCells().map((cell) => (
        <div
          key={cell.id}
          data-table-cell
          data-pinned={cell.column.getIsPinned() ? "true" : "false"}
          className={cn(
            "items-center table-cell align-middle whitespace-nowrap text-sm text-gray-700 group-hover:bg-gray-50",
            {
              "group-data-[scroll=true]/table:shadow-[inset_-1px_0_0_#ececec]":
                cell.column.getIsPinned() &&
                cell.column.getPinnedIndex() === columnPinning.length - 1,
              "sticky z-[1]": cell.column.getIsPinned(),
              "bg-white":
                cell.column.getIsPinned() && row.original.requiredAction,
              "bg-[#FBFBFB]":
                cell.column.getIsPinned() && !row.original.requiredAction,
            },
          )}
          style={{
            left: cell.column.getIsPinned()
              ? cell.column.getStart()
              : undefined,
            width: cell.column.getIsPinned()
              ? cell.column.getSize()
              : undefined,
            minWidth: cell.column.getIsPinned()
              ? cell.column.getSize()
              : undefined,
            maxWidth: cell.column.getIsPinned()
              ? cell.column.getSize()
              : undefined,
          }}
        >
          {/* Table cell */}
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </div>
      ))}
    </NavLink>
  );
};
